import React from "react";
import { Plus, Trash2, Star, X } from "lucide-react";
import { PRICING_UNITS, UNIT_LABELS, formatPlanPrice, planModel, currencySymbol } from "@/lib/pricing";

/**
 * Editable list of pricing tiers ("plans") for a single app.
 *
 * Each plan supports three pricing models:
 *   - "free"      → no price fields
 *   - "flat"      → monthly + yearly fixed amount
 *   - "per_unit"  → optional base + per-unit price (e.g. €5 + €2/horse/mo)
 *
 * Controlled component — parent owns the `plans` array via
 * `value` and `onChange(nextPlans)`.
 */
const emptyPlan = () => ({
  name: "",
  currency: "EUR",
  model: "flat",
  monthly: 0,
  yearly: 0,
  // per_unit fields (kept null/empty when model !== "per_unit")
  unit: "horse",
  unit_custom: "",
  unit_price_monthly: 0,
  unit_price_yearly: 0,
  description: "",
  features: [],
  popular: false,
});

const num = (v) => (v === "" || v == null ? 0 : parseFloat(v) || 0);

export const PlansEditor = ({ value = [], onChange, isNl = true }) => {
  const lang = isNl ? "nl" : "en";
  const update = (idx, patch) => {
    const next = value.map((p, i) => (i === idx ? { ...p, ...patch } : { ...p }));
    onChange(next);
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const add = () => onChange([...value, emptyPlan()]);
  const togglePopular = (idx) => {
    onChange(value.map((p, i) => ({ ...p, popular: i === idx ? !p.popular : false })));
  };
  const addFeature = (idx) => update(idx, { features: [...(value[idx].features || []), ""] });
  const updateFeature = (idx, fIdx, v) => {
    const features = [...(value[idx].features || [])];
    features[fIdx] = v;
    update(idx, { features });
  };
  const removeFeature = (idx, fIdx) => {
    const features = (value[idx].features || []).filter((_, i) => i !== fIdx);
    update(idx, { features });
  };

  return (
    <div className="space-y-3" data-testid="plans-editor">
      {value.length === 0 && (
        <p className="text-xs text-[#94A3B8] italic">
          {isNl ? "Geen plannen toegevoegd. Voeg er één toe om jouw modules + prijzen te tonen." : "No plans added yet. Add one to show your modules + pricing."}
        </p>
      )}
      {value.map((p, idx) => {
        const model = p.model || "flat";
        const preview = formatPlanPrice(p, lang);
        const modelMeta = {
          free: { label: isNl ? "Gratis" : "Free", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
          flat: { label: isNl ? "Vast" : "Flat", cls: "bg-sky-100 text-sky-700 border-sky-200" },
          per_unit: { label: isNl ? "Per eenheid" : "Per unit", cls: "bg-violet-100 text-violet-700 border-violet-200" },
        }[model] || { label: model, cls: "bg-slate-100 text-slate-700 border-slate-200" };
        const planTitle = (p.name || "").trim() || (isNl ? `Plan ${idx + 1}` : `Plan ${idx + 1}`);
        return (
          <div key={idx} className={`border rounded-xl p-4 ${p.popular ? "border-amber-400 bg-amber-50/40" : "border-[#E2E8F0] bg-white"}`} data-testid={`plan-${idx}`}>
            {/* Header: collapsible-style summary line — plan name + live price + model badge */}
            <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-dashed border-[#E2E8F0]">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] shrink-0">{`#${idx + 1}`}</div>
                <div className="font-bold text-[#0F172A] truncate" data-testid={`plan-title-${idx}`}>{planTitle}</div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${modelMeta.cls}`}>{modelMeta.label}</span>
                {p.popular && (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Star size={9} className="fill-white" /> {isNl ? "Populair" : "Popular"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right hidden sm:block">
                  <div className="text-sm font-bold text-[#0F172A] tabular-nums leading-tight" data-testid={`plan-preview-${idx}`}>{preview.primary}</div>
                  {preview.secondary && <div className="text-[11px] text-[#94A3B8] tabular-nums leading-tight">{preview.secondary}</div>}
                </div>
                <button type="button" onClick={() => togglePopular(idx)} className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full border ${p.popular ? "bg-amber-500 border-amber-500 text-white" : "border-[#E2E8F0] text-[#64748B] hover:border-amber-400 hover:text-amber-600"}`} data-testid={`plan-popular-${idx}`}>
                  <Star size={11} className={p.popular ? "fill-white" : ""} /> {isNl ? "Populair" : "Popular"}
                </button>
                <button type="button" onClick={() => remove(idx)} className="text-[#94A3B8] hover:text-red-500 p-1" data-testid={`plan-remove-${idx}`} aria-label="Remove plan"><Trash2 size={14} /></button>
              </div>
            </div>

            {/* Row 1: name + currency + model */}
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_0.7fr_1.2fr] gap-3 mb-3">
              <div>
                <label className="overline block mb-1">{isNl ? "Naam" : "Name"}</label>
                <input className="hac-input" value={p.name} maxLength={40} placeholder={isNl ? "bv. Free, Pro, Team" : "e.g. Free, Pro, Team"} onChange={(e) => update(idx, { name: e.target.value })} data-testid={`plan-name-${idx}`} />
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Valuta" : "Currency"}</label>
                <select className="hac-input" value={p.currency || "EUR"} onChange={(e) => update(idx, { currency: e.target.value })} data-testid={`plan-currency-${idx}`}>
                  <option value="EUR">EUR €</option>
                  <option value="USD">USD $</option>
                  <option value="GBP">GBP £</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Prijsmodel" : "Pricing model"}</label>
                <div className="inline-flex bg-[#F1F5F9] rounded-full p-0.5 w-full" data-testid={`plan-model-${idx}`}>
                  {[
                    { v: "free", l_en: "Free", l_nl: "Gratis" },
                    { v: "flat", l_en: "Flat", l_nl: "Vast" },
                    { v: "per_unit", l_en: "Per unit", l_nl: "Per eenheid" },
                  ].map((m) => (
                    <button
                      key={m.v}
                      type="button"
                      onClick={() => update(idx, { model: m.v })}
                      className={`flex-1 text-[11px] font-bold px-2 py-1.5 rounded-full transition-colors ${model === m.v ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"}`}
                      data-testid={`plan-model-${idx}-${m.v}`}
                    >
                      {isNl ? m.l_nl : m.l_en}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 2: prices — conditional on model */}
            {model === "flat" && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="overline block mb-1">{isNl ? "Per maand" : "Per month"}</label>
                  <input type="number" min={0} step="0.01" className="hac-input" value={p.monthly ?? 0} onChange={(e) => update(idx, { monthly: num(e.target.value) })} data-testid={`plan-monthly-${idx}`} />
                </div>
                <div>
                  <label className="overline block mb-1">{isNl ? "Per jaar" : "Per year"}</label>
                  <input type="number" min={0} step="0.01" className="hac-input" value={p.yearly ?? 0} onChange={(e) => update(idx, { yearly: num(e.target.value) })} data-testid={`plan-yearly-${idx}`} />
                </div>
              </div>
            )}

            {model === "per_unit" && (
              <div className="space-y-3 mb-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
                <div className="text-[11px] text-[#64748B] leading-snug">
                  {(() => {
                    const s = currencySymbol(p.currency);
                    return isNl
                      ? `Bv. ${s}5 basis + ${s}2 per paard per maand. Laat basis op 0 voor 'puur per eenheid'.`
                      : `E.g. ${s}5 base + ${s}2 per horse per month. Leave base at 0 for 'pure per-unit'.`;
                  })()}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline block mb-1">{isNl ? "Basis/maand" : "Base/month"}</label>
                    <input type="number" min={0} step="0.01" className="hac-input" value={p.monthly ?? 0} onChange={(e) => update(idx, { monthly: num(e.target.value) })} data-testid={`plan-base-monthly-${idx}`} />
                  </div>
                  <div>
                    <label className="overline block mb-1">{isNl ? "Basis/jaar" : "Base/year"}</label>
                    <input type="number" min={0} step="0.01" className="hac-input" value={p.yearly ?? 0} onChange={(e) => update(idx, { yearly: num(e.target.value) })} data-testid={`plan-base-yearly-${idx}`} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] gap-3">
                  <div>
                    <label className="overline block mb-1">{isNl ? "Per eenheid type" : "Unit type"}</label>
                    <select className="hac-input" value={p.unit || "horse"} onChange={(e) => update(idx, { unit: e.target.value })} data-testid={`plan-unit-${idx}`}>
                      {PRICING_UNITS.map((u) => (
                        <option key={u} value={u}>{UNIT_LABELS[lang][u]}{u === "custom" ? "…" : ""}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-1">{isNl ? "Prijs/eenheid/mnd" : "Price/unit/mo"}</label>
                    <input type="number" min={0} step="0.01" className="hac-input" value={p.unit_price_monthly ?? 0} onChange={(e) => update(idx, { unit_price_monthly: num(e.target.value) })} data-testid={`plan-unit-monthly-${idx}`} />
                  </div>
                  <div>
                    <label className="overline block mb-1">{isNl ? "Prijs/eenheid/jr" : "Price/unit/yr"}</label>
                    <input type="number" min={0} step="0.01" className="hac-input" value={p.unit_price_yearly ?? 0} onChange={(e) => update(idx, { unit_price_yearly: num(e.target.value) })} data-testid={`plan-unit-yearly-${idx}`} />
                  </div>
                </div>
                {p.unit === "custom" && (
                  <div>
                    <label className="overline block mb-1">{isNl ? "Eenheid naam" : "Unit name"}</label>
                    <input className="hac-input" value={p.unit_custom || ""} maxLength={30} placeholder={isNl ? "bv. locatie, instructeur, klant" : "e.g. location, instructor, client"} onChange={(e) => update(idx, { unit_custom: e.target.value })} data-testid={`plan-unit-custom-${idx}`} />
                  </div>
                )}
              </div>
            )}

            {/* Row 3: description */}
            <div className="mb-3">
              <label className="overline block mb-1">{isNl ? "Korte omschrijving" : "Short description"}</label>
              <input className="hac-input" value={p.description || ""} maxLength={280} placeholder={isNl ? "Wie is dit plan voor?" : "Who is this plan for?"} onChange={(e) => update(idx, { description: e.target.value })} data-testid={`plan-desc-${idx}`} />
            </div>

            {/* Row 4: features */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="overline">{isNl ? "Functionaliteiten" : "Features"}</label>
                <button type="button" onClick={() => addFeature(idx)} className="text-xs text-[#2563EB] hover:underline inline-flex items-center gap-1" data-testid={`plan-feature-add-${idx}`}>
                  <Plus size={11} /> {isNl ? "Functie toevoegen" : "Add feature"}
                </button>
              </div>
              <div className="space-y-1.5">
                {(p.features || []).map((f, fIdx) => (
                  <div key={fIdx} className="flex items-center gap-2">
                    <input className="hac-input flex-1" value={f} maxLength={120} placeholder={isNl ? "bv. GPS gait analysis" : "e.g. GPS gait analysis"} onChange={(e) => updateFeature(idx, fIdx, e.target.value)} data-testid={`plan-feature-${idx}-${fIdx}`} />
                    <button type="button" onClick={() => removeFeature(idx, fIdx)} className="text-[#94A3B8] hover:text-red-500 p-1.5" aria-label="Remove feature" data-testid={`plan-feature-remove-${idx}-${fIdx}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {value.length < 8 && (
        <button type="button" onClick={add} className="w-full border-2 border-dashed border-[#CBD5E1] hover:border-[#2563EB] hover:bg-[#EFF6FF] rounded-xl py-3 text-sm text-[#64748B] hover:text-[#2563EB] inline-flex items-center justify-center gap-1.5" data-testid="plan-add">
          <Plus size={14} /> {isNl ? "Plan toevoegen" : "Add plan"}
        </button>
      )}
    </div>
  );
};

export default PlansEditor;
